/**
 * The create flow as a state machine: nine steps, one draft, and the rules for
 * moving between them. Pure — the page renders it and dispatches actions, and
 * the tests drive it without a browser.
 *
 * Issues have three weights. An `error` is a value the API would refuse (a
 * malformed landing path, a budget that does not add up): it blocks leaving
 * its step and blocks saving. `required` is something a launch needs but a
 * draft can live without (a channel). A `warning` is worth a second look on
 * the Review step and blocks nothing.
 */

import {
  CHANNEL_TYPE_LABELS,
  CREATIVE_SLOTS,
  isOwnedChannel,
  kindOf,
  REFERENCE_MAX,
  type AutomationAction,
  type AutomationTrigger,
  type CampaignAudience,
  type CampaignDraft,
  type CampaignObjective,
  type ChannelDraft,
  type ChannelKind,
  type ChannelType,
  type CreativeDraft,
  type SaveIntent,
  type TrackingDraft,
} from '@/api/campaigns'

import { defaultTrackingDraft, landingPathError, normalisePromoCode, utmValueError } from '../tracking'

export const WIZARD_STEPS = [
  { key: 'basics', label: 'Basics' },
  { key: 'objective', label: 'Objective' },
  { key: 'channels', label: 'Channels' },
  { key: 'audience', label: 'Audience' },
  { key: 'budget', label: 'Budget' },
  { key: 'creative', label: 'Creative' },
  { key: 'tracking', label: 'Tracking' },
  { key: 'automation', label: 'Automation' },
  { key: 'review', label: 'Review' },
] as const
export type StepKey = (typeof WIZARD_STEPS)[number]['key']

export const REVIEW_STEP = WIZARD_STEPS.length - 1

export type IssueSeverity = 'error' | 'required' | 'warning'

export interface DraftIssue {
  step: StepKey
  /** The field it concerns, channel-scoped as `<channelKey>.<field>`. */
  field: string
  message: string
  severity: IssueSeverity
}

export interface WizardState {
  step: number
  /** The furthest step reached; any step up to it can be revisited directly. */
  furthest: number
  draft: CampaignDraft
  /** Steps the user tried to leave with problems, so their messages show. */
  attempted: StepKey[]
}

export type WizardAction =
  | { type: 'next' }
  | { type: 'back' }
  | { type: 'goTo'; step: number }
  | { type: 'setBasics'; patch: Partial<Pick<CampaignDraft, 'name' | 'description' | 'websiteId' | 'startsAt' | 'endsAt' | 'competitionIds'>> }
  | { type: 'setObjective'; objective: CampaignObjective }
  | { type: 'setCustomObjective'; text: string }
  | { type: 'toggleChannel'; channelType: Exclude<ChannelType, 'custom'> }
  | { type: 'addCustomChannel' }
  | { type: 'removeChannel'; key: string }
  | { type: 'renameChannel'; key: string; name: string }
  | { type: 'setChannelKind'; key: string; kind: ChannelKind }
  | { type: 'setChannelBudget'; key: string; budget: number | null }
  | { type: 'setTotalBudget'; budget: number | null }
  | { type: 'setAudience'; patch: Partial<CampaignAudience> }
  | { type: 'setCreative'; key: string; patch: Partial<CreativeDraft> }
  | { type: 'setTracking'; key: string; patch: Partial<TrackingDraft> }
  | { type: 'resetTracking'; key: string }
  | { type: 'toggleAutomation'; trigger: AutomationTrigger }
  | { type: 'setAutomationAction'; trigger: AutomationTrigger; action: AutomationAction }

export function emptyDraft(websiteId = ''): CampaignDraft {
  return {
    websiteId,
    name: '',
    description: '',
    competitionIds: [],
    startsAt: '',
    endsAt: '',
    objective: null,
    customObjective: '',
    channels: [],
    audience: { include: [], exclude: [], geography: [] },
    totalBudget: null,
    automations: [],
  }
}

export function initialWizardState(websiteId = ''): WizardState {
  return { step: 0, furthest: 0, draft: emptyDraft(websiteId), attempted: [] }
}

export function newChannel(channelType: ChannelType, key: string, name: string, kind: ChannelKind = kindOf(channelType)): ChannelDraft {
  return {
    key,
    channelType,
    name,
    kind,
    budget: null,
    creative: { headline: '', body: '', assetUrl: '' },
    tracking: defaultTrackingDraft(channelType, kind, name),
  }
}

const ASSET_URL = /^https?:\/\/\S+$/
/** A channel's creative is named "<channel> — <slot>", and a creative name is at most 200 characters. */
export const CHANNEL_NAME_MAX = 150
const GEO_PREFIX_LENGTH = 'geo:'.length

/** `Money` in `schemas.py` has two decimal places. */
function hasPence(value: number): boolean {
  return Math.abs(value * 100 - Math.round(value * 100)) < 1e-6
}

/** Everything wrong with or missing from `draft`, in step order. */
export function draftIssues(draft: CampaignDraft): DraftIssue[] {
  const issues: DraftIssue[] = []
  const add = (step: StepKey, field: string, message: string, severity: IssueSeverity) =>
    issues.push({ step, field, message, severity })

  // 1 Basics
  if (!draft.name.trim()) add('basics', 'name', 'Give the campaign a name.', 'error')
  else if (draft.name.trim().length > 200) add('basics', 'name', 'Keep the name to 200 characters.', 'error')
  if (!draft.websiteId) add('basics', 'websiteId', 'Choose the website this campaign sends people to.', 'error')
  if (draft.startsAt && draft.endsAt && draft.endsAt < draft.startsAt) {
    add('basics', 'endsAt', 'The end date must not be before the start date.', 'error')
  }
  if (!draft.startsAt || !draft.endsAt) {
    add('basics', 'startsAt', 'No start or end date: a launched campaign starts now and runs until you complete it.', 'warning')
  }
  if (draft.competitionIds.length === 0) add('basics', 'competitionIds', 'No competitions linked.', 'warning')

  // 2 Objective
  if (!draft.objective) add('objective', 'objective', 'Choose an objective.', 'error')
  else if (draft.objective === 'custom' && !draft.customObjective.trim()) {
    add('objective', 'customObjective', 'Describe the custom objective.', 'error')
  }

  // 3 Channels
  if (draft.channels.length === 0) add('channels', 'channels', 'Choose at least one channel.', 'required')
  for (const channel of draft.channels) {
    if (!channel.name.trim()) add('channels', `${channel.key}.name`, 'Name the custom channel.', 'error')
    else if (channel.name.trim().length > CHANNEL_NAME_MAX) add('channels', `${channel.key}.name`, `Keep channel names to ${CHANNEL_NAME_MAX} characters.`, 'error')
  }

  // 4 Audience
  const both = draft.audience.include.filter((segment) => draft.audience.exclude.includes(segment))
  if (both.length > 0) add('audience', 'exclude', 'A segment cannot be both included and excluded.', 'error')
  if (draft.audience.geography.some((place) => place.length + GEO_PREFIX_LENGTH > REFERENCE_MAX)) {
    add('audience', 'geography', `Keep each place to ${REFERENCE_MAX - GEO_PREFIX_LENGTH} characters.`, 'error')
  }
  if (draft.audience.include.length === 0) add('audience', 'include', 'No segments included: every visitor is in the audience.', 'warning')

  // 5 Budget
  if (draft.totalBudget !== null && draft.totalBudget < 0) add('budget', 'totalBudget', 'A budget cannot be negative.', 'error')
  else if (draft.totalBudget !== null && !hasPence(draft.totalBudget)) add('budget', 'totalBudget', 'Use at most two decimal places.', 'error')
  let planned = 0
  for (const channel of draft.channels) {
    if (isOwnedChannel(channel.channelType)) continue
    if (channel.budget === null) {
      add('budget', `${channel.key}.budget`, `${channel.name || 'A channel'} has no budget.`, 'warning')
    } else if (channel.budget < 0) {
      add('budget', `${channel.key}.budget`, 'A budget cannot be negative.', 'error')
    } else if (!hasPence(channel.budget)) {
      add('budget', `${channel.key}.budget`, 'Use at most two decimal places.', 'error')
    } else {
      planned += channel.budget
    }
  }
  if (draft.totalBudget !== null && planned > draft.totalBudget) {
    add('budget', 'totalBudget', 'Channel budgets add up to more than the campaign total.', 'error')
  }

  // 6 Creative
  for (const channel of draft.channels) {
    const { headline, body, assetUrl } = channel.creative
    if (assetUrl.trim() && !ASSET_URL.test(assetUrl.trim())) {
      add('creative', `${channel.key}.assetUrl`, 'An asset URL starts with http:// or https://.', 'error')
    }
    if (!headline.trim() && !body.trim() && !assetUrl.trim()) {
      add('creative', `${channel.key}.creative`, `${channel.name || 'A channel'} has no ${CREATIVE_SLOTS[channel.channelType].label.toLowerCase()} yet.`, 'warning')
    }
  }

  // 7 Tracking
  const codes = new Map<string, string>()
  for (const channel of draft.channels) {
    const tracking = channel.tracking
    const label = channel.name || 'A channel'
    const pathError = landingPathError(tracking.landingPath)
    if (pathError) add('tracking', `${channel.key}.landingPath`, pathError, 'error')
    for (const [field, value, name] of [
      ['utmSource', tracking.utmSource, 'utm_source'],
      ['utmMedium', tracking.utmMedium, 'utm_medium'],
      ['utmContent', tracking.utmContent, 'utm_content'],
    ] as const) {
      const error = utmValueError(value, name)
      if (error) add('tracking', `${channel.key}.${field}`, error, 'error')
    }
    if (tracking.promoMode === 'existing') {
      const code = normalisePromoCode(tracking.promoCode)
      if (!code) {
        add('tracking', `${channel.key}.promoCode`, 'A promo code is 3 to 32 letters, digits and single inner hyphens.', 'error')
      } else if (codes.has(code)) {
        add('tracking', `${channel.key}.promoCode`, `${label} uses the same promo code as ${codes.get(code)}.`, 'error')
      } else {
        codes.set(code, label)
      }
    }
    if (channel.kind === 'offline' && !tracking.shortLink && !tracking.qrCode && tracking.promoMode === 'none') {
      add('tracking', `${channel.key}.attribution`, `${label} has no short link, QR code or promo code, so its results cannot be attributed.`, 'warning')
    }
  }

  return issues
}

export function issuesFor(issues: readonly DraftIssue[], step: StepKey): DraftIssue[] {
  return issues.filter((issue) => issue.step === step)
}

function blocksStep(issue: DraftIssue): boolean {
  return issue.severity !== 'warning'
}

export function canLeaveStep(draft: CampaignDraft, step: number): boolean {
  const key = WIZARD_STEPS[step]?.key
  return key ? !draftIssues(draft).some((issue) => issue.step === key && blocksStep(issue)) : true
}

export function canSave(draft: CampaignDraft, intent: SaveIntent): boolean {
  return !draftIssues(draft).some((issue) => issue.severity === 'error' || (intent === 'launch' && issue.severity === 'required'))
}

function updateChannel(draft: CampaignDraft, key: string, update: (channel: ChannelDraft) => ChannelDraft): CampaignDraft {
  return { ...draft, channels: draft.channels.map((channel) => (channel.key === key ? update(channel) : channel)) }
}

function withDraft(state: WizardState, draft: CampaignDraft): WizardState {
  return { ...state, draft }
}

export function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  const { draft } = state
  switch (action.type) {
    case 'next': {
      if (state.step >= REVIEW_STEP) return state
      const key = WIZARD_STEPS[state.step]?.key as StepKey
      if (!canLeaveStep(draft, state.step)) {
        return state.attempted.includes(key) ? state : { ...state, attempted: [...state.attempted, key] }
      }
      const step = state.step + 1
      return { ...state, step, furthest: Math.max(state.furthest, step) }
    }
    case 'back':
      return state.step === 0 ? state : { ...state, step: state.step - 1 }
    case 'goTo': {
      if (action.step < 0 || action.step > REVIEW_STEP) return state
      // Forward jumps pass through every step in between, so none can be skipped with problems.
      for (let step = state.step; step < action.step; step += 1) {
        if (step >= state.furthest && !canLeaveStep(draft, step)) {
          const key = WIZARD_STEPS[step]?.key as StepKey
          return {
            ...state,
            step,
            furthest: Math.max(state.furthest, step),
            attempted: state.attempted.includes(key) ? state.attempted : [...state.attempted, key],
          }
        }
      }
      return { ...state, step: action.step, furthest: Math.max(state.furthest, action.step) }
    }
    case 'setBasics':
      return withDraft(state, { ...draft, ...action.patch })
    case 'setObjective':
      return withDraft(state, { ...draft, objective: action.objective })
    case 'setCustomObjective':
      return withDraft(state, { ...draft, customObjective: action.text })
    case 'toggleChannel': {
      const existing = draft.channels.some((channel) => channel.key === action.channelType)
      if (existing) return withDraft(state, { ...draft, channels: draft.channels.filter((channel) => channel.key !== action.channelType) })
      const label = CHANNEL_TYPE_LABELS[action.channelType]
      return withDraft(state, { ...draft, channels: [...draft.channels, newChannel(action.channelType, action.channelType, label)] })
    }
    case 'addCustomChannel': {
      let index = 1
      while (draft.channels.some((channel) => channel.key === `custom-${index}`)) index += 1
      return withDraft(state, { ...draft, channels: [...draft.channels, newChannel('custom', `custom-${index}`, '', 'offline')] })
    }
    case 'removeChannel':
      return withDraft(state, { ...draft, channels: draft.channels.filter((channel) => channel.key !== action.key) })
    case 'renameChannel':
      return withDraft(
        state,
        updateChannel(draft, action.key, (channel) => ({ ...channel, name: action.name })),
      )
    case 'setChannelKind':
      return withDraft(
        state,
        updateChannel(draft, action.key, (channel) =>
          channel.channelType === 'custom'
            ? { ...channel, kind: action.kind, tracking: { ...defaultTrackingDraft('custom', action.kind, channel.name), landingPath: channel.tracking.landingPath } }
            : channel,
        ),
      )
    case 'setChannelBudget':
      return withDraft(state, updateChannel(draft, action.key, (channel) => ({ ...channel, budget: action.budget })))
    case 'setTotalBudget':
      return withDraft(state, { ...draft, totalBudget: action.budget })
    case 'setAudience':
      return withDraft(state, { ...draft, audience: { ...draft.audience, ...action.patch } })
    case 'setCreative':
      return withDraft(
        state,
        updateChannel(draft, action.key, (channel) => ({ ...channel, creative: { ...channel.creative, ...action.patch } })),
      )
    case 'setTracking':
      return withDraft(
        state,
        updateChannel(draft, action.key, (channel) => ({ ...channel, tracking: { ...channel.tracking, ...action.patch } })),
      )
    case 'resetTracking':
      return withDraft(
        state,
        updateChannel(draft, action.key, (channel) => ({
          ...channel,
          tracking: defaultTrackingDraft(channel.channelType, channel.kind, channel.name),
        })),
      )
    case 'toggleAutomation': {
      const on = draft.automations.some((rule) => rule.trigger === action.trigger)
      return withDraft(state, {
        ...draft,
        automations: on
          ? draft.automations.filter((rule) => rule.trigger !== action.trigger)
          : [...draft.automations, { trigger: action.trigger, action: 'notify_team' }],
      })
    }
    case 'setAutomationAction':
      return withDraft(state, {
        ...draft,
        automations: draft.automations.map((rule) => (rule.trigger === action.trigger ? { ...rule, action: action.action } : rule)),
      })
  }
}
